import "./searchPanel.scss";

import _ from "lodash";
import PropTypes from "prop-types";
import React, { Fragment, PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { PopoverBody } from "reactstrap";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormSearchField from "../../components/form/FormSearchField";
import FormTextField from "../../components/form/FormTextField";
import Icon from "../../components/interface/Icon";
import NavigationToolbar from "../../components/navigationToolbar";
import { popoverOrPortal } from "../../components/portal/popoverOrPortal";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { isScreenModeMobile } from "../../constants/screenModes";
import { PropTypeShapes } from "../../types";
import { wrapEditingComponent } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";

class SearchPanelHeader extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      popoverOpen: false,
      saveName: null
    };
    this.togglePopover = this.togglePopover.bind(this);
    this.onReplaceValueChange = this.onReplaceValueChange.bind(this);
    this.onSearchReplaceAll = this.onSearchReplaceAll.bind(this);
  }

  togglePopover() {
    this.setState({
      popoverOpen: !this.state.popoverOpen
    });
  }

  renderSearchSettings() {
    const isIos = preprocessorQuery.isIos();
    return (
      <div className="searchSettings">
        {isIos ? (
          <div className="searchSettingsGroup">
            <FormCheckbox
              key="searchCase"
              label="Case sensitive"
              checkboxState={this.props.caseSensitive}
              onChange={this.props.onCaseSensitiveChange}
            />
            <FormCheckbox
              key="searchWholeWord"
              label="Match whole words only"
              checkboxState={this.props.wholeWord}
              onChange={this.props.onWholeWordChange}
            />
          </div>
        ) : null}
        <div className="searchSettingsGroup">
          <div className="searchSettingsGroupHeader">Search for:</div>
          {this.props.allTypes
            .map(t => (
              <FormCheckbox
                key={t.id}
                label={t.label}
                checkboxState={!this.props.excludedSearchTypes.contains(t.id)}
                onChange={value => this.props.onToggleType(t.id, value)}
              />
            ))
            .toArray()}
        </div>
        <div className="searchSettingsGroup">
          <div className="searchSettingsGroupHeader">Search in:</div>
          {this.props.allFields
            .map((name, id) => (
              <FormCheckbox
                key={id}
                label={name}
                checkboxState={!this.props.excludedSearchFields.contains(id)}
                onChange={value => this.props.onToggleField(id, value)}
              />
            ))
            .toList()
            .toArray()}
        </div>
      </div>
    );
  }

  renderSearchLimits() {
    const searchFor = this.props.excludedSearchTypes.count()
      ? this.props.allTypes
        .filter(t => !this.props.excludedSearchTypes.contains(t.id))
        .map(t => t.label)
        .toList()
        .toArray()
      : [];
    const searchIn = this.props.excludedSearchFields.count()
      ? this.props.allFields
        .filter((name, id) => !this.props.excludedSearchFields.contains(id))
        .toList()
        .toArray()
      : [];

    const noTypesToSearch = searchFor.length === 0 && this.props.excludedSearchTypes.count() > 0;
    const noFieldsToSearch = searchIn.length === 0 && this.props.excludedSearchFields.count() > 0;

    const warning = noTypesToSearch
      ? "Warning: all search types are disabled"
      : noFieldsToSearch
        ? "Warning: all search fields are disabled"
        : `Search only in ${_.concat(searchFor, searchIn).join(", ")}`;
    return (
      <div className="searchPanelLimits" onClick={this.togglePopover}>
        {warning}
      </div>
    );
  }

  renderPopover() {
    const settingsId = "searchPanelSettingsIcon";

    let placement = "right-start";
    let hideArrow = false;
    let alwaysPortal = false;
    //#!if tablet === true
// placement = "right-start";
    //#!endif

    let popoverOptions = {
      className: `searchSettingsPopover`,
      placement,
      hideArrow,
      isOpen: this.state.popoverOpen,
      target: settingsId,
      toggle: this.togglePopover
    };

    const child = (
      <Fragment>
        {this.props.screenContext && isScreenModeMobile(this.props.screenContext.screenMode) ? (
          <NavigationToolbar
            title="Search Settings"
            showBack={true}
            showBackArrow={true}
            onBackClick={this.togglePopover}
            backText="Back"
            flexLeft={1}
            flexCenter={3}
            flexRight={1}
          />
        ) : null}
        <PopoverBody onClick={e => e.stopPropagation()}>{this.renderSearchSettings()}</PopoverBody>
      </Fragment>
    );

    return popoverOrPortal(child, popoverOptions, alwaysPortal);
  }

  onReplaceValueChange(replaceValue) {
    if (this.props.onReplaceValueChange) {
      this.props.onReplaceValueChange(replaceValue);
    }
  }

  onSearchReplaceAll() {
    if (this.props.onSearchReplaceAll) {
      this.props.onSearchReplaceAll(
        this.props.searchValue,
        this.props.replaceValue,
        this.props.caseSensitive,
        this.props.wholeWord,
        this.props.excludedSearchTypes,
        this.props.excludedSearchFields,
        this.props.propertiesById
      );
    }
  }

  render() {
    const isIos = preprocessorQuery.isIos();
    const settingsId = "searchPanelSettingsIcon";
    const countLabel = `${this.props.matchCount === 0 ? "No" : this.props.matchCount} result${
      this.props.matchCount !== 1 ? "s" : ""
    }`;
    const searchLabel = `Search${this.props.searchValue ? " - " + countLabel : ""}`;

    return (
      <div>
        <div key="header" className="searchPanelHeader">
          <span>{searchLabel}</span>
          <Icon
            color="iconMono"
            icon="options"
            onClick={this.togglePopover}
            id={settingsId}
            tooltip="Configure Search Options"
          />
        </div>
        <div className="searchPanelFields">
          <div key="searchField" className="searchPanelField searchField">
            <FormSearchField
              multiline={false}
              value={this.props.searchValue}
              caseSensitive={this.props.caseSensitive}
              wholeWord={this.props.wholeWord}
              placeholder="Search"
              autoFocus={true}
              onChange={this.props.onSearchValueChange}
              onCaseSensitiveChange={this.props.onCaseSensitiveChange}
              onWholeWordChange={this.props.onWholeWordChange}
              showCaseSensitiveIcon={!isIos}
              showWholeWordIcon={!isIos}
            />
            {wrapEditingComponent(
              <Icon
                color="iconMono"
                icon={this.props.replaceExpanded ? "upArrow" : "downArrow"}
                onClick={() => this.props.toggleReplaceExpanded(!this.props.replaceExpanded)}
                tooltip="Toggle Search/Replace"
              />
            )}
          </div>
          {wrapEditingComponent(
            this.props.replaceExpanded ? (
              <div key="replaceField" className="searchPanelField replaceField">
                <FormTextField
                  multiline={false}
                  placeholder="Replace with"
                  value={this.props.replaceValue}
                  onChange={this.onReplaceValueChange}
                />
                <Icon color="iconMono" icon="sidebarSync" onClick={this.onSearchReplaceAll} tooltip="Replace all" />
              </div>
            ) : null
          )}
          {this.props.excludedSearchFields.count() || this.props.excludedSearchTypes.count()
            ? this.renderSearchLimits()
            : null}
        </div>
        {this.state.popoverOpen ? <div className="filterSavePopoverBackdrop" onClick={this.togglePopover} /> : null}
        {this.state.popoverOpen ? this.renderPopover() : null}
      </div>
    );
  }
}

SearchPanelHeader.propTypes = {
  searchValue: PropTypes.string,
  replaceValue: PropTypes.string,
  caseSensitive: PropTypes.bool.isRequired,
  wholeWord: PropTypes.bool.isRequired,
  replaceExpanded: PropTypes.bool.isRequired,
  matchCount: PropTypes.number.isRequired,
  onCaseSensitiveChange: PropTypes.func.isRequired,
  onWholeWordChange: PropTypes.func.isRequired,
  onSearchValueChange: PropTypes.func.isRequired,
  onReplaceValueChange: PropTypes.func,
  toggleReplaceExpanded: PropTypes.func.isRequired,
  onSearchReplaceAll: PropTypes.func,
  allowReplace: PropTypes.bool.isRequired,
  propertiesById: ImmutablePropTypes.map.isRequired,

  allTypes: PropTypeShapes.itemTypeList.isRequired,
  allFields: ImmutablePropTypes.map.isRequired,
  excludedSearchTypes: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  excludedSearchFields: ImmutablePropTypes.listOf(PropTypes.string).isRequired,

  onToggleType: PropTypes.func.isRequired,
  onToggleField: PropTypes.func.isRequired,

  //#!if iOS === true || harvard === true
  // From withScreenContext
  screenContext: PropTypes.object
  //#!endif
};

let exportedView = SearchPanelHeader;
//#!if iOS === true || harvard === true
exportedView = withScreenContext(SearchPanelHeader);
//#!endif
export default exportedView;
